import { Lead } from 'services/types/lead';

import baseApi from './baseApi';

interface LeadsResponse {
  rows: Lead[];
  count: number;
}

interface LeadsWithPaginationResponse extends LeadsResponse {
  pagination: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

interface AggregatedLeadsResponse {
  aggregatedLeads: Array<{
    sourceId: string;
    sourceType: string;
    leadCount: number;
    lastLeadDate: string;
  }>;
  totalLeads: number;
  totalAdsWithLeads: number;
}

interface KanbanLeadsResponse {
  leadsByStatus: Record<string, Lead[]>;
  totalLeads: number;
  statusCounts: Record<string, number>;
  paginationInfo: Record<
    string,
    {
      page: number;
      hasMore: boolean;
      totalCount: number;
    }
  >;
  pageSize: number;
}

interface KanbanStatusCountsResponse {
  statusCounts: Record<string, number>;
  totalLeads: number;
}

const leadsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getLeads: build.query<
      LeadsResponse,
      {
        businessUnitId?: number | null;
        paginationModel?: string;
        filterModel?: string;
        sortModel?: string;
        includeFullDetails?: boolean;
      }
    >({
      query: (params) => ({
        url: '/leads',
        method: 'GET',
        params: {
          ...(params.businessUnitId ? { businessUnitId: params.businessUnitId } : {}),
          ...(params.paginationModel ? { paginationModel: params.paginationModel } : {}),
          ...(params.filterModel ? { filterModel: params.filterModel } : {}),
          ...(params.sortModel ? { sortModel: params.sortModel } : {}),
          ...(params.includeFullDetails ? { includeFullDetails: params.includeFullDetails } : {}),
        },
      }),
      transformResponse: (response: LeadsResponse) => response,
    }),
    getAggregatedLeadsByBusinessUnit: build.query<
      AggregatedLeadsResponse,
      {
        businessUnitId: number;
      }
    >({
      query: (params) => ({
        url: '/admin/leads/aggregatedByBusinessUnit',
        method: 'GET',
        params: {
          businessUnitId: params.businessUnitId,
        },
      }),
      transformResponse: (response: AggregatedLeadsResponse) => response,
    }),
    getLeadsByBusinessUnitWithSource: build.query<
      LeadsWithPaginationResponse,
      {
        businessUnitId: number;
        limit?: number;
        offset?: number;
      }
    >({
      query: (params) => ({
        url: '/admin/leads/byBusinessUnitWithSource',
        method: 'GET',
        params: {
          businessUnitId: params.businessUnitId,
          ...(params.limit ? { limit: params.limit } : {}),
          ...(params.offset ? { offset: params.offset } : {}),
        },
      }),
      transformResponse: (response: LeadsWithPaginationResponse) => response,
    }),
    getLeadsKanban: build.query<
      KanbanLeadsResponse,
      {
        businessUnitId: number;
        startDate?: string;
        endDate?: string;
        pageSize?: number;
        statusPages?: Record<string, number>;
      }
    >({
      query: (params) => ({
        url: '/leads/kanban',
        method: 'GET',
        params: {
          businessUnitId: params.businessUnitId,
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
          ...(params.pageSize ? { pageSize: params.pageSize } : {}),
          ...(params.statusPages ? { statusPages: JSON.stringify(params.statusPages) } : {}),
        },
      }),
      transformResponse: (response: KanbanLeadsResponse) => response,
    }),
    getLeadsKanbanStatusCounts: build.query<
      KanbanStatusCountsResponse,
      {
        businessUnitId: number;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: (params) => ({
        url: '/leads/kanban/statusCounts',
        method: 'GET',
        params: {
          businessUnitId: params.businessUnitId,
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
        },
      }),
      transformResponse: (response: KanbanStatusCountsResponse) => response,
    }),
    downloadLeads: build.mutation({
      query: (params: {
        businessUnitId?: number | null;
        paginationModel?: string;
        filterModel?: string;
        sortModel?: string;
        includeFullDetails?: boolean;
      }) => ({
        url: `/leads`,
        method: 'GET',
        params: {
          ...(params.businessUnitId ? { businessUnitId: params.businessUnitId } : {}),
          ...(params.paginationModel ? { paginationModel: params.paginationModel } : {}),
          ...(params.filterModel ? { filterModel: params.filterModel } : {}),
          ...(params.sortModel ? { sortModel: params.sortModel } : {}),
          ...(params.includeFullDetails ? { includeFullDetails: params.includeFullDetails } : {}),
        },
      }),
      transformResponse: (response: { rows: Lead[]; count: number }) => {
        return {
          leads: response.rows,
          total: response.count,
        };
      },
    }),
    getOutboundCampaignIds: build.query({
      query: (params: { businessUnitId: number; startDate: string | null; endDate: string | null }) => ({
        url: `/leads/outboundCampaignIds`,
        method: 'GET',
        params: {
          ...(params.businessUnitId ? { businessUnitId: params.businessUnitId } : {}),
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
        },
      }),
      transformResponse: (response: string[]) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetLeadsQuery,
  useGetAggregatedLeadsByBusinessUnitQuery,
  useGetLeadsByBusinessUnitWithSourceQuery,
  useGetOutboundCampaignIdsQuery,
  useDownloadLeadsMutation,
  useGetLeadsKanbanQuery,
  useGetLeadsKanbanStatusCountsQuery,
} = leadsApi;
